import {
  CalendarEvent,
  SelectManyQuestion,
  SelectOneQuestion,
  ShortAnswerQuestion
} from "../models";
import {
  ProductSharedSettingsBuilder,
  ProductAdvancedSettingsBuilder,
  ProductInventorySettingsBuilder
} from "../product/builders";
import * as moment from "moment-timezone";
import { QuestionType } from "../product/enums";
import { CustomPurchaseWindow } from "../product/models/custom-purchase-window";
import { ProductBundleSettingsBuilder } from "../product/builders/product.bundle.builder";
import * as firebase from "firebase";

export class CalendarEventBuilder {
  public id?: string;
  public productId: string;
  public shared: ProductSharedSettingsBuilder;
  public advanced: ProductAdvancedSettingsBuilder;
  public bundle: ProductBundleSettingsBuilder;
  public inventory: ProductInventorySettingsBuilder;
  public isCustomEvent: boolean;
  public sold: number;
  public rank: number;
  public date: Date;
  public timezone: string;
  public isEditing: boolean;
  public isDuplicating: boolean;

  constructor() {
    this.isEditing = false;
    this.isDuplicating = false;
    this.isCustomEvent = false;
    this.date = null;
    this.sold = 0;
    this.rank = 100;
    this.shared = new ProductSharedSettingsBuilder();
    this.advanced = new ProductAdvancedSettingsBuilder();
    this.inventory = new ProductInventorySettingsBuilder();
    this.bundle = new ProductBundleSettingsBuilder();
  }

  build = (): CalendarEvent => new CalendarEvent(this);

  duplicate(event: CalendarEvent) {
    this.productId = event.productId;
    this.isDuplicating = true;
    this.isCustomEvent = event.isCustomEvent;
    this.advanced.edit(event.advanced);
    this.shared.edit(event.shared);
    this.inventory.edit(event.inventory);
    this.bundle.edit(event.bundle);
    this.timezone = event.timezone;
    this.sold = 0;
    return this;
  }

  edit(event: CalendarEvent) {
    this.id = event.id;
    this.productId = event.productId;
    this.isEditing = true;
    this.isCustomEvent = event.isCustomEvent;
    this.advanced.edit(event.advanced);
    this.shared.edit(event.shared);
    this.inventory.edit(event.inventory);
    this.bundle.edit(event.bundle);
    this.rank = event.rank;
    this.date = event.date;
    this.timezone = event.timezone;
    this.sold = event.sold;
    return this;
  }

  hasAdvancedSettings(): boolean {
    return !this.advanced.isNull();
  }

  loadAPIResponse(data: any): CalendarEventBuilder {
    this.id = data.id;
    this.productId = data.productId;
    this.isCustomEvent = data.isCustomEvent ?? false;
    this.sold = data.sold;
    this.date = new Date(data.date);
    this.rank = data.rank ?? 100;
    this.advanced.load(data.advanced);
    this.inventory.load(data.inventory);
    this.shared.load(data.shared);
    this.bundle.load(data.bundle);
    this.timezone = data.timezone;
    return this;
  }

  setDate(date: Date): CalendarEventBuilder {
    this.date = date;
    return this;
  }

  get questions() {
    return this.shared.questions;
  }

  setTimezone(tz: string): CalendarEventBuilder {
    this.timezone = tz;
    return this;
  }

  public create(form: any, questions?: any): CalendarEventBuilder {
    //STEP 1: HANDLE DATE
    this.isCustomEvent = true;
    this.setDate(
      moment(form.date)
        .startOf("D")
        .set("hours", 4)
        .toDate()
    );
    //STEP 2: HANDLE PRICE
    this.inventory.setPrice(form.price);
    //STEP 3: HANDLE MAX
    this.inventory.setMaxAvailibility(form.hasMax ? form.max : null);
    //STEP 4: HANDLE LIMIT
    this.inventory.setLimit(form.hasLimit ? form.limit : null);
    //STEP 5: HANDLE DESCRIPTION
    this.shared.description = form.hasDescription ? form.description : null;
    //STEP 6: HANDLE INSTRUCTIONS
    this.shared.instructions = form.hasInstructions ? form.instructions : null;
    //STEP 7: HANDLE WARNING
    this.shared.warning = form.hasWarning ? form.warning : null;
    //STEP 8: HANDLE QUESTIONS
    if (questions != [])
      for (let i = 0; i < questions.length; i++) {
        let f = questions[i].value;
        switch (f.type as QuestionType) {
          case QuestionType.SelectMany:
            this.shared.questions.push(
              new SelectManyQuestion(f.question, f.options)
            );
            break;
          case QuestionType.SelectOne:
            this.shared.questions.push(
              new SelectOneQuestion(f.question, f.options)
            );
            break;
          default:
            this.shared.questions.push(new ShortAnswerQuestion(f.question));
        }
      }
    //STEP 9: HANDLE ADVANCED SETTINGS
    if (form.hasAdvanced) {
      this.advanced.setVisibility(form.visibleInApp, form.visibleInAppToggle);
      this.advanced.setRedeemWindow(
        form.redemptionWindowToggle,
        form.redeemFrom,
        form.redeemTo,
        form.redeemDaysAfter
      );

      this.advanced.setPurchaseWindow(form.purchaseDaysBeforeEvent);
    } else {
      this.advanced.setVisibility(null, null);
      this.advanced.setRedeemWindow(null, null, null, null);
      this.advanced.setPurchaseWindow(new CustomPurchaseWindow());
    }
    return this;
  }
}
